import { useState, useEffect, useCallback, useRef } from 'react'
import { glorifyApiClient, GlorifyApiError, imageFileUrl, publicImageUrl } from '../../../services/api/glorify'
import type { ImageRecord } from '../../../services/api/glorify'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function statusChipClass(statusId: number): string {
  switch (statusId) {
    case 1: return 'gd-status-chip gd-status-chip--processing'
    case 2: return 'gd-status-chip gd-status-chip--completed'
    case 3: return 'gd-status-chip gd-status-chip--failed'
    default: return 'gd-status-chip gd-status-chip--pending'
  }
}

function statusLabel(statusId: number): string {
  switch (statusId) {
    case 0: return 'Pending'
    case 1: return 'Processing'
    case 2: return 'Completed'
    case 3: return 'Failed'
    default: return 'Unknown'
  }
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UploadState = 'idle' | 'uploading' | 'success' | 'error'

// ─── Component ───────────────────────────────────────────────────────────────

export default function ImagesPage(): React.JSX.Element {
  // ── List state ────────────────────────────────────────────────────────────
  const [images, setImages] = useState<ImageRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [listError, setListError] = useState<string | null>(null)

  // ── Upload state ──────────────────────────────────────────────────────────
  const [uploadState, setUploadState] = useState<UploadState>('idle')
  const [uploadMessage, setUploadMessage] = useState<string>('')
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // ── Per-row state ─────────────────────────────────────────────────────────
  /** Image IDs with a visibility toggle request currently in flight. */
  const [togglingIds, setTogglingIds] = useState<Set<number>>(new Set())
  /** Auto-dismissing error for per-row failures. */
  const [actionError, setActionError] = useState<string | null>(null)
  /**
   * Image IDs where the "Copy public URL" button is in the brief "Copied"
   * feedback state (icon swaps to check for ~1.5 s).
   */
  const [copiedIds, setCopiedIds] = useState<Set<number>>(new Set())

  // Auto-dismiss action error after 4 s
  useEffect(() => {
    if (!actionError) return
    const timer = setTimeout(() => setActionError(null), 4000)
    return () => clearTimeout(timer)
  }, [actionError])

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchImages = useCallback(async (): Promise<void> => {
    setListError(null)
    try {
      const resp = await glorifyApiClient.listImages()
      setImages(resp.images)
    } catch (err) {
      setListError(
        err instanceof GlorifyApiError ? err.message : 'Failed to load images'
      )
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchImages()
  }, [fetchImages])

  // ── Upload ────────────────────────────────────────────────────────────────
  const handleUpload = useCallback(
    async (file: File): Promise<void> => {
      setUploadState('uploading')
      setUploadMessage(`Uploading ${file.name}…`)
      try {
        const resp = await glorifyApiClient.uploadImage(file)
        setUploadState('success')
        setUploadMessage(`Uploaded "${resp.filename}" (${formatFileSize(resp.size)})`)
        await fetchImages()
        // Auto-reset after 3 s so the zone is ready again
        setTimeout(() => setUploadState('idle'), 3000)
      } catch (err) {
        setUploadState('error')
        setUploadMessage(
          err instanceof GlorifyApiError ? err.message : 'Upload failed'
        )
      }
    },
    [fetchImages]
  )

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    if (file) void handleUpload(file)
    // Reset so the same file can be re-uploaded
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file && uploadState !== 'uploading') void handleUpload(file)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (): void => setIsDragging(false)

  // ── Visibility toggle — optimistic update + rollback on error ─────────────
  const handleVisibilityToggle = async (img: ImageRecord): Promise<void> => {
    const snapshot = images
    const newValue = !img.is_public
    setTogglingIds((prev) => new Set([...prev, img.image_id]))
    setImages((prev) =>
      prev.map((i) => (i.image_id === img.image_id ? { ...i, is_public: newValue } : i))
    )
    try {
      const updated = await glorifyApiClient.setImageVisibility(img.image_id, newValue)
      setImages((prev) => prev.map((i) => (i.image_id === img.image_id ? updated : i)))
    } catch (err) {
      setImages(snapshot) // rollback
      setActionError(
        'Failed to update visibility: ' +
          (err instanceof GlorifyApiError ? err.message : 'Unknown error')
      )
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev)
        next.delete(img.image_id)
        return next
      })
    }
  }

  // ── Copy public URL ───────────────────────────────────────────────────────
  const handleCopyPublicUrl = async (img: ImageRecord): Promise<void> => {
    try {
      await navigator.clipboard.writeText(publicImageUrl(img.image_file_name))
      setCopiedIds((prev) => new Set([...prev, img.image_id]))
      setTimeout(() => {
        setCopiedIds((prev) => {
          const next = new Set(prev)
          next.delete(img.image_id)
          return next
        })
      }, 1500)
    } catch {
      setActionError('Failed to copy URL to clipboard')
    }
  }

  return (
    <>
      <div className="gd-page-header">
        <h1>Images</h1>
        <p>
          Upload and manage your image files. Toggling an image public makes it
          immediately accessible via its public URL — no extra publish step required.
        </p>
      </div>

      {/* Action error banner — auto-dismisses after 4 s */}
      {actionError && (
        <div className="gd-action-error" role="alert">
          <span className="material-icons" aria-hidden="true">error_outline</span>
          {actionError}
        </div>
      )}

      {/* Upload zone */}
      <div
        className={`gd-upload-zone${isDragging ? ' gd-upload-zone--dragging' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => uploadState !== 'uploading' && fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label="Upload image — click or drag and drop"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            if (uploadState !== 'uploading') fileInputRef.current?.click()
          }
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          aria-hidden="true"
        />
        <div>
          <span className="material-icons" aria-hidden="true">cloud_upload</span>
        </div>
        <p>
          <strong>Click to upload</strong> or drag and drop an image here
        </p>
        <p style={{ fontSize: 12 }}>PNG, JPG, GIF, WebP, SVG…</p>
      </div>

      {/* Upload status */}
      {uploadState !== 'idle' && (
        <div
          className={`gd-upload-status gd-upload-status--${uploadState}`}
          role={uploadState === 'error' ? 'alert' : 'status'}
        >
          <span className="material-icons" aria-hidden="true">
            {uploadState === 'uploading'
              ? 'hourglass_top'
              : uploadState === 'success'
                ? 'check_circle'
                : 'error'}
          </span>
          {uploadMessage}
        </div>
      )}

      {/* List error */}
      {listError && (
        <div className="gd-banner gd-banner--error" role="alert">
          <span className="material-icons" aria-hidden="true">warning</span>
          <p>{listError}</p>
        </div>
      )}

      {/* Table or empty state */}
      {isLoading ? (
        <div className="gd-empty">
          <div className="gd-spinner" aria-label="Loading images" />
        </div>
      ) : images.length === 0 ? (
        <div className="gd-empty">
          <span className="material-icons" aria-hidden="true">image_not_supported</span>
          <p>No images yet. Upload your first image above.</p>
        </div>
      ) : (
        <div className="gd-table-wrap">
          <table className="gd-table">
            <thead>
              <tr>
                <th>Preview</th>
                <th>File name</th>
                <th>Type</th>
                <th>Size</th>
                <th>Status</th>
                <th>Public</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {images.map((img) => (
                <tr key={img.image_id}>
                  {/* Thumbnail — always shown via authenticated file URL */}
                  <td style={{ width: 56 }}>
                    <img
                      src={imageFileUrl(img.image_id)}
                      alt={img.image_file_name}
                      width={40}
                      height={40}
                      style={{
                        objectFit: 'cover',
                        borderRadius: 4,
                        display: 'block',
                        background: 'var(--md-sys-color-surface-container-high)',
                      }}
                      onError={(e) => {
                        // File missing on disk — fall back to placeholder icon
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                        const placeholder = target.nextSibling as HTMLElement | null
                        if (placeholder) placeholder.style.display = 'block'
                      }}
                    />
                    <span
                      className="material-icons"
                      aria-hidden="true"
                      style={{
                        fontSize: 24,
                        color: 'var(--md-sys-color-outline)',
                        display: 'none',
                      }}
                    >
                      image
                    </span>
                  </td>

                  <td>{img.image_file_name}</td>
                  <td className="gd-cell-muted">{img.image_file_type}</td>
                  <td className="gd-cell-muted">{formatFileSize(img.image_file_size)}</td>
                  <td>
                    <span className={statusChipClass(img.image_status_id)}>
                      {statusLabel(img.image_status_id)}
                    </span>
                  </td>

                  {/* Public toggle — MD3-style switch */}
                  <td>
                    <label
                      className={[
                        'gd-switch',
                        img.is_public ? 'gd-switch--on' : '',
                        togglingIds.has(img.image_id) ? 'gd-switch--disabled' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-label={`Toggle public visibility for ${img.image_file_name}`}
                    >
                      <input
                        type="checkbox"
                        className="gd-switch__input"
                        checked={img.is_public}
                        disabled={togglingIds.has(img.image_id)}
                        onChange={() => void handleVisibilityToggle(img)}
                      />
                      <span className="gd-switch__track" aria-hidden="true">
                        <span className="gd-switch__thumb" />
                      </span>
                    </label>
                  </td>

                  {/* Actions — copy public URL (only for public images) */}
                  <td style={{ width: 48 }}>
                    {img.is_public && (
                      <button
                        className="gd-btn gd-btn--icon"
                        style={{ width: 36, height: 36 }}
                        onClick={() => void handleCopyPublicUrl(img)}
                        aria-label="Copy public URL"
                        title="Copy public URL"
                      >
                        <span className="material-icons" aria-hidden="true">
                          {copiedIds.has(img.image_id) ? 'check' : 'link'}
                        </span>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
